<script setup lang="ts">
import { computed, type Component } from 'vue';

/**
 * IconTile — turns any Lucide (or other SVG) icon into a "3D-feeling"
 * focal point: rotated white tile with a soft drop-shadow stack and
 * an optional animated halo behind it. Used to give the app's hero
 * rows, modal headers and empty states a bold visual metaphor while
 * the rest of the UI stays calm and minimal.
 *
 * Usage:
 *   <IconTile :icon="Trophy" size="lg" tone="teal" halo />
 */

const props = withDefaults(defineProps<{
    icon: Component;
    /** Tile size — sm 2.2rem / md 2.8rem / lg 3.6rem / xl 5rem. */
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /** Surrounding pulse glow. */
    halo?: boolean;
    /** Visual tone of the glyph inside the tile. */
    tone?: 'teal' | 'amber' | 'rose' | 'indigo' | 'emerald';
    /** Optional override of the inline icon size in px. */
    iconSize?: number;
}>(), {
    size: 'md',
    halo: false,
    tone: 'teal',
});

const iconPxBySize: Record<string, number> = {
    sm: 16, md: 20, lg: 24, xl: 36,
};
const resolvedIconPx = computed(() => props.iconSize ?? iconPxBySize[props.size]);

const toneColor = computed(() => {
    switch (props.tone) {
        case 'amber':    return '#d97706';
        case 'rose':     return '#e11d48';
        case 'indigo':   return '#4f46e5';
        case 'emerald':  return '#059669';
        case 'teal':
        default:         return '#0d9488';
    }
});
</script>

<template>
    <span :class="halo ? 'icon-tile-halo' : ''">
        <span :class="['icon-tile', 'icon-tile-' + size]" :style="{ color: toneColor }">
            <component :is="icon" :size="resolvedIconPx" :stroke-width="2.2" />
        </span>
    </span>
</template>
